'use client'

/**
 * SELL TIME on /alerts (register: sell-time-delay-model-scope-1004). When the server says this user is on the
 * delayed free feed (subscription-status `delayedFeed`), PremiumContentGate renders the feed instead of the
 * daily-limit wall, wrapped in this provider, and each alert card reads it to say when members saw it.
 * Absent provider = real time, so every other surface and user is unchanged.
 *
 * Copy rules: honest and plain. The stamp states a fact (the time and price the scanner flagged it), never a
 * promise, and the banner names the trade-off once. No price, no pressure, one quiet link.
 */
import Link from 'next/link'
import { createContext, useContext, type ReactNode } from 'react'
import { Clock3 } from 'lucide-react'

type DelayedFeed = { minutes: number } | null
const Ctx = createContext<DelayedFeed>(null)

/** minutes <= 0 provides null (real time). Always rendered by the gate so the tree shape never changes when
 *  the status arrives, which would otherwise remount the feed and reconnect its socket. */
export function DelayedFeedProvider({ minutes, children }: { minutes: number; children: ReactNode }) {
  return <Ctx.Provider value={minutes > 0 ? { minutes } : null}>{children}</Ctx.Provider>
}

/** { minutes } when this viewer is on the delayed feed, else null. */
export function useDelayedFeed(): DelayedFeed {
  return useContext(Ctx)
}

export function DelayedFeedBanner({ minutes }: { minutes: number }) {
  return (
    <div className="mx-auto mb-3 flex w-full max-w-5xl flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2 text-[13px]">
      <span className="flex items-center gap-2 text-amber-200">
        <Clock3 className="h-4 w-4 shrink-0 text-amber-400" aria-hidden />
        <span>
          <strong className="font-semibold text-amber-100">Free feed: {minutes} minutes behind.</strong>{' '}
          <span className="text-amber-200/80">Members get every alert as it fires.</span>
        </span>
      </span>
      <Link prefetch={false} href="/subscribe?from=delayed-feed" className="text-xs font-semibold text-amber-300 underline-offset-2 hover:underline">
        See it in real time
      </Link>
    </div>
  )
}

/** One line under an alert: when (and at what price) members got it. Renders nothing on the real-time feed. */
export function DelayedFeedStamp({ firedAtMs, price }: { firedAtMs: number | null | undefined; price: number | null | undefined }) {
  const d = useDelayedFeed()
  if (!d || !firedAtMs || !Number.isFinite(firedAtMs)) return null
  const time = new Date(firedAtMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
  const px = typeof price === 'number' && Number.isFinite(price) && price > 0
    ? (price < 1 ? `$${price.toFixed(3)}` : `$${price.toFixed(2)}`)
    : null
  return (
    <div className="mt-1 flex items-center gap-1.5 text-[11px] text-emerald-300/90">
      <Clock3 className="h-3 w-3 shrink-0" aria-hidden />
      <span>Members saw this at {time} ET{px ? ` at ${px}` : ''}.</span>
    </div>
  )
}
